import { dia } from '@joint/plus';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Chip, Stack, Tooltip, Typography } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useCellOutputSchema } from './useCellOutputSchema';

/**
 * UpstreamFieldsHint — Pedacinho de UI reusável que detecta se o `textValue` do campo
 * referencia uma variável do fluxo e, em caso afirmativo, mostra chips com os CAMPOS
 * do shape real da ação upstream (traversando o grafo via useCellOutputSchema).
 *
 * Introduzido em 2026-10-13 pra alimentar as 5 caixas consumidoras com a mesma UX
 * sem duplicar lógica em cada inspector.
 *
 * Formatos de placeholder reconhecidos no `textValue`:
 *   {{variable.<nome>}}, {{variable.<nome>.<campo>}}  → sintaxe moderna (prioridade)
 *   @<nome>@                                           → sintaxe legada
 *
 * Pega o PRIMEIRO nome encontrado — se o campo tem múltiplas vars, admin itera resolvendo
 * uma por vez.
 *
 * Fallback silencioso: sem placeholder OU sem upstream detectado → não renderiza nada
 * (zero poluição visual).
 */

interface Props {
    cell: dia.Cell | null | undefined;
    textValue: string;
    /** Callback quando admin clica num chip — recebe o token completo pronto pra inserir. */
    onInsert?: (token: string) => void;
    /** Variante compacta — usada em FieldRenderer onde já há label próprio. */
    compact?: boolean;
}

const VARIABLE_BRACE_RE = /\{\{\s*variable\.([a-zA-Z0-9_-]+)/;
const VARIABLE_AT_RE = /@([a-zA-Z0-9_-]+)@/;

const UpstreamFieldsHint = ({ cell, textValue, onInsert, compact }: Props): ReactElement | null => {
    const intl = useIntl();

    const detectedVariable = useMemo<string>(() => {
        const s = typeof textValue === 'string' ? textValue : '';
        const m1 = s.match(VARIABLE_BRACE_RE);
        if (m1?.[1]) return m1[1];
        const m2 = s.match(VARIABLE_AT_RE);
        if (m2?.[1]) return m2[1];
        return '';
    }, [textValue]);

    const upstream = useCellOutputSchema({ variableName: detectedVariable, cell });

    if (!detectedVariable || !upstream.slug || upstream.fields.length === 0) {
        return null;
    }

    const buildToken = (field: string) => `{{variable.${detectedVariable}.${field}}}`;

    const content = (
        <>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {intl.formatMessage({
                    id: 'upstreamhint.label',
                    defaultMessage: '✨ Campos de "{var}" (de {action}):',
                }, {
                    var: detectedVariable,
                    action: upstream.displayName ?? upstream.slug,
                })}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {upstream.fields.map((f) => {
                    const token = buildToken(f.name);
                    const chip = (
                        <Chip
                            key={f.name}
                            label={`.${f.name}`}
                            size="small"
                            variant="outlined"
                            onClick={() => onInsert?.(token)}
                            sx={{
                                height: 20,
                                fontSize: 10,
                                fontFamily: 'monospace',
                                cursor: onInsert ? 'pointer' : 'default',
                            }}
                        />
                    );
                    return f.title ? <Tooltip key={f.name} title={f.title}>{chip}</Tooltip> : chip;
                })}
            </Stack>
            {upstream.isArray && upstream.itemFields.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                    {intl.formatMessage({
                        id: 'upstreamhint.arrayNote',
                        defaultMessage: 'Esta variável é um ARRAY — use `.{arrayKey}.0.<campo>` pra 1º item ou use numa caixa Lista Interativa.',
                    }, { arrayKey: upstream.arrayKey ?? 'array' })}
                </Typography>
            )}
        </>
    );

    if (compact) {
        return (
            <div style={{ marginTop: 4, fontSize: 11, color: '#555', paddingLeft: 8, borderLeft: '2px solid #8BC34A' }}>
                {content}
            </div>
        );
    }

    return (
        <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ fontSize: 12, py: 0.5 }}
        >
            {content}
        </Alert>
    );
};

export default UpstreamFieldsHint;
